import * as React from 'react';

import { DRAG_X_OFFSET, DRAG_Y_OFFSET } from '../utils/constants';
import {
    DragAndDroppable,
    DragData,
    DragViewState,
    DraggableItemTypes,
    DropViewState,
    createDragViewState,
    createDropViewState,
} from 'owa-dnd';
import { updateCalendarDisplayOrder, updateCalendarGroupDisplayOrder } from 'owa-calendar-update';

import type { CalendarPickerItemDragData } from '../store/schema/CalendarPickerItemDragData';
import type CalendarPickerScenarioId from '../store/schema/CalendarPickerScenarioId';
import { findCalendarGroup } from 'owa-calendar-surface-calendars-store';
import getDraggedItemData from '../selectors/getDraggedItemData';
import isCalendarGroupCollapsed from '../selectors/isCalendarGroupCollapsed';
import { observer } from 'mobx-react-lite';
import { setDraggedItemData } from '../mutators/draggedItemDataMutators';
import { toggleCalendarGroup } from '../mutators/collapseCalendarGroupMutators';

const styles = require('./CalendarPicker.scss');
const classNames = require('classnames/bind').bind(styles);

export interface CalendarPickerDragAndDroppableItemProps {
    itemId: string;
    itemType: string;
    userIdentity: string;
    displayName: string;
    parentGroupId?: string;
    scenarioId: CalendarPickerScenarioId;
    previousItemId: string;
    children?: React.ReactNode;
}

export default observer(function CalendarPickerDragAndDroppableItem(
    props: CalendarPickerDragAndDroppableItemProps
) {
    const [dropViewState, setDropViewState] = React.useState<DropViewState>(createDropViewState());
    const [dragViewState] = React.useState<DragViewState>(createDragViewState());
    const timerId = React.useRef<number>();

    // All drag functions and the variables inside them get memoized at the very beginning
    // of the drag. So once the drag starts, if the previousItemId prop changes, the values
    // of the memoized variable does not actually update when the onDrag or onDrop functions
    // get called. Therefore, a ref is needed to act as an instance variable so you have
    // the most up to date value.
    const prevItemId = React.useRef<string>();

    const dragData: CalendarPickerItemDragData = {
        itemType: props.itemType,
        displayName: props.displayName,
        itemId: props.itemId,
        userIdentity: props.userIdentity,
    };

    React.useEffect(() => {
        prevItemId.current = props.previousItemId;
    }, [props.previousItemId]);

    // Clear timer on unmount
    React.useEffect(() => {
        return () => {
            window.clearTimeout(timerId.current);
            timerId.current = 0;
        };
    }, []);

    const onDragStart = () => {
        setDraggedItemData(props.scenarioId, dragData);
    };

    const onDragEnd = () => {
        setDraggedItemData(props.scenarioId, null);
    };

    const onDragOver = (dragData: DragData) => {
        setDropViewState({
            isDragOver: true,
            draggableItemType: '',
        });

        if (
            !timerId.current &&
            props.itemType === DraggableItemTypes.CalendarGroupNode &&
            dragData.itemType === DraggableItemTypes.CalendarNode
        ) {
            timerId.current = window.setTimeout(() => {
                if (dropViewState.isDragOver) {
                    const { scenarioId, userIdentity, itemId } = props;
                    const calendarGroup = findCalendarGroup(itemId, userIdentity);
                    if (
                        isCalendarGroupCollapsed(
                            scenarioId,
                            userIdentity,
                            calendarGroup?.calendarGroupId.id
                        )
                    ) {
                        toggleCalendarGroup(
                            scenarioId,
                            userIdentity,
                            calendarGroup.calendarGroupId.id
                        );
                    }
                }
                timerId.current = 0;
            }, 500);
        }
    };

    const onDragLeave = () => {
        setDropViewState({
            isDragOver: false,
            draggableItemType: '',
        });
    };

    const getDragData = () => {
        return dragData;
    };

    const onDrop = (dragData: DragData) => {
        const itemDragData = dragData as CalendarPickerItemDragData;
        if (dragViewState.isBeingDragged || itemDragData.itemId === prevItemId.current) {
            return;
        }

        switch (dragData.itemType) {
            case DraggableItemTypes.CalendarNode:
                {
                    let previousItemId = prevItemId.current;
                    let nextItemId = props.itemId;
                    let destParentGroupId = props.parentGroupId;

                    // If a calendar is dropped on a group header,
                    // add it to the end of the list.
                    if (props.itemType === DraggableItemTypes.CalendarGroupNode) {
                        previousItemId = undefined;
                        nextItemId = undefined;
                        destParentGroupId = props.itemId;
                    }

                    updateCalendarDisplayOrder(
                        props.userIdentity /* userIdentity */,
                        itemDragData.itemId /* draggedItemId */,
                        previousItemId /* prevItemId */,
                        nextItemId /* nextItemId */,
                        destParentGroupId /* destParentGroupId */
                    );
                }
                break;
            case DraggableItemTypes.CalendarGroupNode:
                {
                    updateCalendarGroupDisplayOrder(
                        props.userIdentity /* userIdentity */,
                        itemDragData.itemId /* draggedItemId */,
                        prevItemId.current /* prevItemId */,
                        props.itemId /* nextItemId */
                    );
                }
                break;
        }
    };

    const canDrop = (): boolean => {
        const dragData = getDraggedItemData(props.scenarioId);

        if (
            dragData &&
            dragData.userIdentity === props.userIdentity &&
            (props.itemType === dragData.itemType || isCalendarDraggedOverGroup())
        ) {
            return true;
        }

        return false;
    };

    const getDragPreview = (dragData: CalendarPickerItemDragData) => {
        const elem = document.createElement('div');
        elem.className = styles.dragHelperStyle;
        elem.innerText = dragData.displayName;
        return elem;
    };

    const isCalendarDraggedOverGroup = (): boolean => {
        const dragData = getDraggedItemData(props.scenarioId);
        return (
            props.itemType === DraggableItemTypes.CalendarGroupNode &&
            dragData.itemType === DraggableItemTypes.CalendarNode
        );
    };

    const isDragOver = dropViewState.isDragOver && !dragViewState.isBeingDragged;
    const containerClassName = classNames({
        [styles.topBorderDragOver]: isDragOver && !isCalendarDraggedOverGroup(),
        showAsHoverOnDragOver: isDragOver && isCalendarDraggedOverGroup(),
    });

    return (
        <DragAndDroppable
            classNames={classNames(styles.dragAndDropContainer, containerClassName)}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            getDragData={getDragData}
            getDragPreview={getDragPreview}
            showDefaultNoDragImage={false}
            xOffset={DRAG_X_OFFSET}
            yOffset={DRAG_Y_OFFSET}
            dragViewState={dragViewState}
            dropViewState={dropViewState}
            canDrop={canDrop}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            preventBubblingDragEvents={true}>
            {props.children}
        </DragAndDroppable>
    );
});
